/* eslint-disable import/order */
// !!!NOTE: Disabling the importing order rule as we want the settings to be imported before any other imports. Maybe fix this in the future.

import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { locale } from "dayjs";
import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import TagManager from "react-gtm-module";
import { HelmetProvider } from "react-helmet-async";
import { RouterProvider } from "react-router-dom";
import "dayjs/locale/fi";
import "dayjs/locale/sv";

// !! IMOPORTANT  !! These needs to be initialized before any @krea/customer-features import //
import "./i18n";

import axios from "axios";
import { isObject } from "lodash";

import HttpService from "@krea/customer-features/services/httpService";
import { queryClient } from "@krea/customer-features/store/queryClient";
import {
  isThirdPartyScriptFrame,
  SENTRY_IGNORE_ERRORS,
} from "@krea/customer-features/utils/sentryIgnoreErrors";

import { router } from "./router";
import settings from "./settings";

locale("sv");

const isDeployedEnvironment = ["production", "staging"].includes(
  settings.environment,
);

// eslint-disable-next-line react-refresh/only-export-components
function Root() {
  useEffect(() => {
    const instance = HttpService.getInstance();
    instance.setupInterceptors();

    const tagManagerArgs = {
      gtmId: settings.gtmId,
      dataLayer: {
        event: "initTracking",
        environment: settings.environment,
      },
    };

    TagManager.initialize(tagManagerArgs);

    if (isDeployedEnvironment) {
      Sentry.init({
        dsn: "https://a9a86248d74b4c7881dec3b30c3ba74a@o242905.ingest.us.sentry.io/5307143",
        // This sets the sample rate to be 10%.
        replaysSessionSampleRate: 0.1,

        // If the entire session is not sampled, use the below sample rate to sample
        // sessions when an error occurs.
        replaysOnErrorSampleRate: 1.0,

        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            networkDetailAllowUrls: ["https://customer-api.krea.se/api/"],
            maskAllText: false,
            maskAllInputs: true,
            blockAllMedia: false,
          }),
        ],

        environment: settings.environment,
        enabled: isDeployedEnvironment,

        ignoreErrors: SENTRY_IGNORE_ERRORS,

        beforeSend(event, hint) {
          // Initialize Sentry

          const error = hint.originalException;

          // Check if the error is an Axios cancellation
          if (axios.isCancel(error)) {
            return null; // Prevent Axios cancel errors from being sent to Sentry
          }

          // Check if the error is a GraphQL request cancellation (AbortController)
          if (
            error &&
            isObject(error) &&
            "name" in error &&
            error.name === "AbortError"
          ) {
            return null; // Prevent GraphQL cancel errors from being sent to Sentry
          }

          // // Handle errors without a message
          // if (
          //   (!event.message || event.message === "No error message") &&
          //   !event.exception?.values?.length
          // ) {
          //   console.warn("Ignored error without a message");

          //   return null;
          // }

          // Ignore third-party script errors
          if (event.exception) {
            const { values } = event.exception;

            if (
              values?.some((exception) =>
                exception.stacktrace?.frames?.some((frame) =>
                  isThirdPartyScriptFrame(frame.filename),
                ),
              )
            ) {
              console.warn("Ignored third-party error:", event.message);

              return null;
            }
          }

          // If it's not a cancellation error, return the event to Sentry
          return event;
        },
      });
    }
  }, []);

  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          {!isDeployedEnvironment && (
            <ReactQueryDevtools initialIsOpen={false} />
          )}
          <RouterProvider router={router} />
        </HelmetProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );
}

createRoot(document.getElementById("root")!).render(<Root />);
